import React, { createContext, useContext } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { DEFAULT_LOCALE, isLocale, Locale } from './Locale.ts';
import { findBySlug, localeOf } from '../Seo/routeSeo.ts';

const LocaleContext = createContext<Locale>(DEFAULT_LOCALE);

/**
 * Resolves `Locale` and provides it down the tree.
 *
 * ADR 0051 §6 named the route as the eventual real source and `uiLang` as a
 * review affordance until localised routes existed; §8 is part 4 making that
 * true. The current path is looked up in the manifest, and its own `locale`
 * (English when the entry sets none) is what a visitor gets. `uiLang` still
 * works as an override on top of that - useful for previewing the catalogue
 * on a route that has no localised counterpart - but it is no longer the
 * primary source, and neither reading nor the override touches
 * `ISettingsProps`, `generateUrl`, `ProcessUrl` or the `s=` seed.
 */
export function LocaleProvider({ children }: { children: React.ReactNode }) {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const route = findBySlug(location.pathname);
    const routeLocale: Locale = route ? localeOf(route) : DEFAULT_LOCALE;

    const requested = searchParams.get('uiLang');
    const locale: Locale = isLocale(requested) ? requested : routeLocale;

    return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>;
}

export function useLocale(): Locale {
    return useContext(LocaleContext);
}

export default LocaleContext;
